"use client";

import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "motion/react";
import { useLazyVideo } from "@/components/motion/useLazyVideo";
import styles from "./Footer.module.scss";

/**
 * The footer CTA's looping desk clip. It sits on every page but below the
 * fold, so useLazyVideo attaches the file only as the footer nears the
 * viewport (with the iOS muted-autoplay contract); the poster overlay keeps
 * the tile from ever showing black when playback is refused, and the corner
 * toggle satisfies WCAG 2.2.2. Under prefers-reduced-motion it holds on the
 * poster with native controls.
 */
export function FooterLoop() {
  const reduceMotion = useReducedMotion() ?? false;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  useLazyVideo(videoRef, "/videos/footer-loop.mp4", {
    mode: "visible",
    reduceMotion,
  });

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const sync = () => setPlaying(!v.paused);
    v.addEventListener("play", sync);
    v.addEventListener("pause", sync);
    sync();
    return () => {
      v.removeEventListener("play", sync);
      v.removeEventListener("pause", sync);
    };
  }, []);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  };

  return (
    <div className={styles.loop}>
      <video
        ref={videoRef}
        className={styles.loopVideo}
        poster="/videos/footer-loop-poster.webp"
        loop
        muted
        playsInline
        controls={reduceMotion}
        preload="none"
        aria-label="Viskhan at his desk"
      />
      {!reduceMotion && (
        /* Same URL as the video's poster attribute, so this costs no extra
           download. It guarantees the tile is never black while playback
           hasn't started. */
        // eslint-disable-next-line @next/next/no-img-element
        <img
          className={styles.loopPoster}
          src="/videos/footer-loop-poster.webp"
          alt=""
          loading="lazy"
          decoding="async"
          aria-hidden="true"
          data-hidden={playing || undefined}
        />
      )}
      {!reduceMotion && (
        <button
          type="button"
          className={styles.loopToggle}
          onClick={toggle}
          aria-label={playing ? "Pause video" : "Play video"}
        >
          {playing ? (
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>
      )}
    </div>
  );
}
